/**
 * Portfolio-wide search index — powers the cross-property global search.
 *
 * Loads a lightweight slice of every entity the signed-in operator can access
 * (apartments, rooms, active occupants, lessors) plus the properties they
 * belong to, so the search box can match and route across the whole portfolio
 * rather than a single property.
 *
 * Access scoping:
 *   - Super / regular admins see every property (RLS grants full read).
 *   - Owner-portal users are narrowed to the properties linked to them in
 *     `property_owners`; apartments / rooms / occupants are then filtered to
 *     that allowed set client-side, so no other owner's data is ever indexed.
 *   - Occupants and tenants (tenant matches) are only fetched for super admins —
 *     the same gating the Rooms / Apartments tabs already use.
 *
 * Two tenant sources, on purpose: `occupants` (one active booking per room)
 * drives the room rows' "Occupied" status and occupant matching, while
 * `tenants` (past, current and future bookings) drives the Tenants group.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { fetchAllRows } from "@/lib/fetch-all-rows";
import {
  NON_TENANT_BOOKING_STATUSES,
  localToday,
  tenantTimeline,
  type TenantTimeline,
} from "@/lib/tenant-timeline";
import { useProperties } from "./useProperties";
import { useLessors } from "./useLessors";
import {
  useAllActiveOccupants,
  type ActiveOccupant,
} from "./useReassignBookings";
import type { Lessor } from "@/types/lessor";

type TenantBookingRow = {
  id: number;
  name: string | null;
  email: string | null;
  phone: string | null;
  room_id: number;
  status: string | null;
  start_date: string | null;
  end_date: string | null;
  admin_archived_at: string | null;
};

export type IndexTenant = {
  bookingId: number;
  name: string | null;
  email: string | null;
  phone: string | null;
  roomId: number;
  timeline: TenantTimeline;
};

export type IndexProperty = {
  id: number;
  title: string;
  address: string | null;
  city: string | null;
};

export type IndexApartment = {
  id: number;
  property_id: number;
  label: string;
  floor: string | null;
  lessor_id: string | null;
};

export type IndexRoom = {
  id: number;
  property_id: number;
  apartment_id: number | null;
  title: string;
  floor: string | null;
  size_sqm: number | null;
  price: number | null;
  is_hidden: boolean;
  status: string | null;
};

export type GlobalSearchIndex = {
  /** propertyId → { title, address } for meta + routing labels. */
  properties: Map<number, IndexProperty>;
  apartments: IndexApartment[];
  rooms: IndexRoom[];
  /** roomId → active occupant (super admins only). */
  occupants: Map<number, ActiveOccupant>;
  /** Past, current and future tenants, one per booking (super admins only). */
  tenants: IndexTenant[];
  lessors: Lessor[];
  /** propertyId → its property-level lessor_id (owner fallback for apartments). */
  propertyLessorId: Map<number, string | null>;
  isLoading: boolean;
};

/**
 * @param enabled Gate the (portfolio-wide) fetches. Callers pass `false` until
 *   the operator actually engages the search, so a property/apartment page load
 *   doesn't eagerly pull the whole portfolio when search is never used.
 * @param includeTenants Also load `tenants` (every past, current and future
 *   booking). Opt-in: only the global search's Tenants group reads it, and it
 *   pages through the whole booking history.
 */
export function useGlobalSearchIndex(
  enabled: boolean = true,
  includeTenants: boolean = false
): GlobalSearchIndex {
  const { user, isSuperAdmin, isOwnerPortalOnly } = useAuth();

  // Accessible properties. Reuses useProperties' owner-scoping (property_owners
  // filter) so owner-portal users only ever see their own portfolio.
  const { data: properties = [], isLoading: propsLoading } = useProperties(
    isOwnerPortalOnly ? (user?.id ?? "__none__") : undefined,
    enabled
  );

  const allowedIds = useMemo(
    () => new Set(properties.map((p) => Number(p.id))),
    [properties]
  );

  const { data: allApartments = [], isLoading: aptLoading } = useQuery({
    queryKey: ["v2", "global-search", "apartments"],
    enabled,
    queryFn: async (): Promise<IndexApartment[]> => {
      const { data, error } = await supabase
        .from("apartments")
        .select("id, property_id, label, floor, lessor_id");
      if (error) throw error;
      return (data ?? []) as IndexApartment[];
    },
  });

  const { data: allRooms = [], isLoading: roomsLoading } = useQuery({
    queryKey: ["v2", "global-search", "rooms"],
    enabled,
    queryFn: async (): Promise<IndexRoom[]> => {
      const { data, error } = await supabase
        .from("rooms")
        .select(
          "id, property_id, apartment_id, title, floor, size_sqm, price, is_hidden, status"
        );
      if (error) throw error;
      return (data ?? []) as IndexRoom[];
    },
  });

  // Lessors are admin-role-gated by RLS ("Admins manage lessors") — owner
  // portal has no SELECT policy on the table, so skip the request rather
  // than have it fail every time an owner uses search.
  const { data: lessors = [], isLoading: lessorsLoading } = useLessors(
    undefined,
    enabled && !isOwnerPortalOnly
  );
  // Tenant matches are super-admin-only (same gating as the tabs).
  const { data: occupants = new Map(), isLoading: occupantsLoading } =
    useAllActiveOccupants(isSuperAdmin && enabled);
  const {
    data: tenantBookings = [],
    isLoading: tenantsLoading,
    isError: tenantsError,
  } = useQuery({
    queryKey: ["v2", "global-search", "tenant-bookings"],
    enabled: isSuperAdmin && enabled && includeTenants,
    // The whole booking history is paged on every fetch; don't repeat it each
    // time the search is engaged on a freshly mounted page.
    staleTime: 60_000,
    queryFn: () =>
      fetchAllRows<TenantBookingRow>(
        "global-search tenant bookings",
        (from, size) =>
          supabase
            .from("bookings")
            .select(
              "id, name, email, phone, room_id, status, start_date, end_date, admin_archived_at"
            )
            .not("status", "in", `(${NON_TENANT_BOOKING_STATUSES.join(",")})`)
            .order("id")
            .range(from, from + size - 1)
      ),
  });

  return useMemo(() => {
    const propMap = new Map<number, IndexProperty>();
    const propLessor = new Map<number, string | null>();
    for (const p of properties) {
      const id = Number(p.id);
      propMap.set(id, {
        id,
        title: p.title,
        address: p.address ?? null,
        city: p.city ?? null,
      });
      propLessor.set(id, p.lessor_id ?? null);
    }

    // Filter every entity to the accessible property set.
    const apartments = allApartments.filter((a) =>
      allowedIds.has(Number(a.property_id))
    );
    const rooms = allRooms.filter((r) => allowedIds.has(Number(r.property_id)));
    // Compare room ids as numbers: PostgREST doesn't consistently narrow
    // bigint ids to the same JS type across separate queries (see
    // PropertiesList).
    const allowedRoomIds = new Set(rooms.map((r) => Number(r.id)));
    const scopedOccupants = new Map<number, ActiveOccupant>();
    for (const [roomId, occ] of occupants) {
      if (allowedRoomIds.has(Number(roomId))) scopedOccupants.set(roomId, occ);
    }
    const today = localToday();
    const tenants: IndexTenant[] = [];
    // If the booking-history read fails, fall back to the active occupants so
    // the Tenants group still lists current tenants rather than going silently
    // empty (it can't tell past or future without that read).
    if (tenantsError) {
      for (const occ of scopedOccupants.values()) {
        tenants.push({
          bookingId: occ.bookingId,
          name: occ.name,
          email: occ.email,
          phone: occ.phone,
          roomId: occ.roomId,
          timeline: "current",
        });
      }
    } else {
      for (const b of tenantBookings) {
        if (!allowedRoomIds.has(Number(b.room_id))) continue;
        const timeline = tenantTimeline(b, today);
        if (!timeline) continue;
        tenants.push({
          bookingId: b.id,
          name: b.name,
          email: b.email,
          phone: b.phone,
          roomId: b.room_id,
          timeline,
        });
      }
    }

    return {
      properties: propMap,
      apartments,
      rooms,
      occupants: scopedOccupants,
      tenants,
      lessors,
      propertyLessorId: propLessor,
      isLoading:
        propsLoading ||
        aptLoading ||
        roomsLoading ||
        lessorsLoading ||
        occupantsLoading ||
        tenantsLoading,
    };
  }, [
    properties,
    allApartments,
    allRooms,
    occupants,
    tenantBookings,
    tenantsError,
    lessors,
    allowedIds,
    propsLoading,
    aptLoading,
    roomsLoading,
    lessorsLoading,
    occupantsLoading,
    tenantsLoading,
  ]);
}
